import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, ExclamationTriangleIcon, DocumentIcon, GlobeAltIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { getPatientFile, getPatient, patientAttachmentUrl, type PatientDetail, type Source } from '../api/unimedica';
import PdfViewer from './PdfViewer';

// Port aus SourceSidebar.svelte — zeigt die angeklickte Quelle: Akten-Dokument
// (PDF + extrahierter Text), Review-PDF, Wissens-PDF oder Web-Link.

export default function SourceSidebar({ source, onClose, onPickSource }:
  { source: Source | null; onClose: () => void; onPickSource?: (s: { patient_id: string; filename: string }) => void }) {
  const navigate = useNavigate();
  const [mdContent, setMdContent] = useState<string | null>(null);
  const [detail, setDetail] = useState<PatientDetail | null>(null);
  const [attachmentName, setAttachmentName] = useState<string | null>(null);
  const [originalName, setOriginalName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showMd, setShowMd] = useState(false);
  const [fileMissing, setFileMissing] = useState(false);

  useEffect(() => {
    if (!source || !('patient_id' in source)) { setMdContent(null); setAttachmentName(null); setOriginalName(null); setDetail(null); setFileMissing(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true); setShowMd(false); setFileMissing(false);
      const md = await getPatientFile(source.patient_id, source.filename);
      const d = await getPatient(source.patient_id);
      if (cancelled) return;
      setMdContent(md); setDetail(d);
      const meta: any = d?.files.find((f) => f.name === source.filename);
      // Anzeigbare Fassung bevorzugen (Word/E-Mail → PDF daneben); Download bleibt das Original.
      setAttachmentName(meta?.has_attachment ? (meta.vorschau ?? meta.attachment ?? null) : (meta?.vorschau ?? null));
      setOriginalName(meta?.dateiname ?? meta?.attachment ?? null);
      // Datei existiert nicht in der Akte → halluzinierte Zitation.
      setFileMissing(!meta && !!d);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [source]);

  if (!source) return null;

  const patientName = 'patient_id' in source
    ? (detail?.stammdaten ? `${detail.stammdaten.vorname ?? ''} ${detail.stammdaten.nachname ?? ''}`.trim() || source.patient_id : source.patient_id)
    : '';
  const baseName = 'filename' in source ? source.filename.replace(/\.md$/, '') : '';
  const headerTitle = 'review_ref' in source || 'kb_slug' in source || 'url' in source ? source.title : patientName;
  const headerSub = 'review_ref' in source ? source.review_ref : 'kb_slug' in source ? source.kb_slug : 'url' in source ? source.url : baseName;

  const Warn = ({ children }: { children: React.ReactNode }) => (
    <div className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2.5">
      <ExclamationTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-700" />
      <div className="text-[11.5px] leading-snug text-amber-800">{children}</div>
    </div>
  );

  return (
    <aside className="card flex h-full flex-col overflow-hidden p-0">
      <header className="flex items-start justify-between gap-3 border-b border-gray-200 bg-white px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-primary-500">Quelle</div>
          <div className="mt-0.5 truncate text-[16px] font-semibold leading-tight tracking-tight text-ps-dark-900" title={headerTitle}>{headerTitle}</div>
          <div className="numerical mt-1 truncate text-[11px] text-ps-dark-400" title={headerSub}>{headerSub}</div>
        </div>
        <button onClick={onClose} className="rounded-lg p-1.5 text-ps-dark-400 hover:bg-gray-100 hover:text-ps-dark-700" aria-label="Schliessen"><XMarkIcon className="h-4 w-4" /></button>
      </header>

      {'review_ref' in source || 'kb_slug' in source ? (
        <div className="min-h-0 flex-1 overflow-hidden"><PdfViewer src={source.pdf_url} title={source.title} subtitle={'review_ref' in source ? source.review_ref : source.kb_slug} /></div>
      ) : 'patient_id' in source ? (
        loading ? (
          <div className="flex flex-1 items-center justify-center"><div className="w-2/3 space-y-2"><div className="shimmer h-2 w-full rounded" /><div className="shimmer h-2 w-5/6 rounded" /><div className="shimmer h-2 w-3/4 rounded" /></div></div>
        ) : fileMissing ? (
          <div className="flex-1 overflow-y-auto">
            <Warn>Die zitierte Datei <span className="numerical font-mono">{source.filename}</span> wurde in dieser Akte nicht gefunden — vermutlich wurde der Name von der KI erfunden. Hier sind die echten Dokumente:</Warn>
            <ul className="divide-y divide-gray-200">
              {(detail?.files ?? []).map((f) => (
                <li key={f.name}>
                  <button type="button" onClick={() => onPickSource?.({ patient_id: source.patient_id, filename: f.name })}
                    className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-[12.5px] text-ps-dark-700 transition-colors hover:bg-gray-50">
                    <DocumentIcon className="h-3 w-3 shrink-0 text-ps-dark-400" /><span className="numerical truncate" title={f.name}>{f.name.replace(/\.md$/, '')}</span>
                  </button>
                </li>
              ))}
            </ul>
            <div className="border-t border-gray-200 bg-white px-4 py-3">
              <button type="button" className="btn-ghost w-full border border-gray-200 text-[12.5px]" onClick={() => navigate(`/patienten/${encodeURIComponent(source.patient_id)}`)}>Patientenakte öffnen</button>
            </div>
          </div>
        ) : attachmentName ? (<>
          <div className="min-h-0 flex-1 overflow-hidden"><PdfViewer src={patientAttachmentUrl(source.patient_id, attachmentName)} title="Original-PDF" subtitle={originalName ?? attachmentName} /></div>
          <div className="border-t border-gray-200 bg-white">
            <button className="flex w-full items-center justify-between px-4 py-2.5 text-left text-[12px] text-ps-dark-700 transition-colors hover:bg-gray-50" onClick={() => setShowMd((s) => !s)}>
              <span className="text-[11px] font-semibold uppercase tracking-wider text-ps-dark-500">Markdown · extrahierter Text</span>
              <ChevronDownIcon className={`h-3 w-3 transition-transform ${showMd ? 'rotate-180' : ''}`} />
            </button>
            {showMd && <pre className="max-h-[40dvh] overflow-y-auto whitespace-pre-wrap px-4 pb-4 pt-1 font-mono text-[11.5px] leading-[1.55] text-ps-dark-700">{mdContent ?? '(leer)'}</pre>}
          </div>
        </>) : (<>
          <Warn>Kein Original-PDF — nur extrahierter Text verfügbar.</Warn>
          <pre className="flex-1 overflow-y-auto whitespace-pre-wrap p-4 font-mono text-[12px] text-ps-dark-700">{mdContent ?? '(leer)'}</pre>
        </>)
      ) : 'url' in source ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
          <GlobeAltIcon className="h-6 w-6 text-ps-dark-400" />
          <a href={source.url} target="_blank" rel="noopener noreferrer" className="btn-ghost border border-gray-200 text-[13px]">In neuem Tab öffnen</a>
        </div>
      ) : null}
    </aside>
  );
}
